import { Field, FieldInput, FieldLabel, FieldSelect } from '@ibe/ui';
import { useEffect, useState, type ReactNode } from 'react';
import {
  Controller,
  useWatch,
  type Control,
  type FieldErrors,
  type UseFormRegister,
} from 'react-hook-form';

import {
  GUEST_ACCOUNT_STATUS_OPTIONS,
  GUEST_STATUS_OPTIONS,
  type GuestFormInput,
} from '../api/schemas.js';
import { useCities, useCountries, useStates } from '../api/queries.js';

export interface GuestFieldsProps {
  readonly register: UseFormRegister<GuestFormInput>;
  readonly errors: FieldErrors<GuestFormInput>;
  readonly control: Control<GuestFormInput>;
  readonly hasAccount: boolean;
}

function Section({
  title,
  children,
}: {
  readonly title: string;
  readonly children: ReactNode;
}): React.JSX.Element {
  return (
    <fieldset className="space-y-4 border-t border-border pt-4 first:border-t-0 first:pt-0">
      <legend className="sr-only">{title}</legend>
      <h2 className="text-body font-semibold">{title}</h2>
      {children}
    </fieldset>
  );
}

function Row({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>;
}

const GUEST_ADDRESS_TYPE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'HOME', label: 'Home' },
  { value: 'WORK', label: 'Work' },
  { value: 'BILLING', label: 'Billing' },
  { value: 'OTHER', label: 'Other' },
];

const SEARCH_DEBOUNCE_MS = 300;

function useDebouncedSearchTerm(value: string, delay = SEARCH_DEBOUNCE_MS): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(value);
    }, delay);
    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);
  return debounced;
}

export function GuestFields({
  register,
  errors,
  control,
  hasAccount,
}: GuestFieldsProps): React.JSX.Element {
  const countryCode = useWatch<GuestFormInput, 'address.countryCode'>({
    control,
    name: 'address.countryCode',
  });
  const stateCode = useWatch<GuestFormInput, 'address.state'>({ control, name: 'address.state' });

  const [countrySearch, setCountrySearch] = useState('');
  const [stateSearch, setStateSearch] = useState('');
  const [citySearch, setCitySearch] = useState('');

  const debouncedCountrySearch = useDebouncedSearchTerm(countrySearch);
  const debouncedStateSearch = useDebouncedSearchTerm(stateSearch);
  const debouncedCitySearch = useDebouncedSearchTerm(citySearch);

  const countriesQuery = useCountries(debouncedCountrySearch);
  const statesQuery = useStates(countryCode ?? '', debouncedStateSearch);
  const citiesQuery = useCities(countryCode ?? '', stateCode ?? '', debouncedCitySearch);

  const countryOptions = (countriesQuery.data ?? []).map((country) => ({
    value: country.code,
    label: country.name,
  }));

  const stateOptions = (statesQuery.data ?? []).map((state) => ({
    value: state.code,
    label: state.name,
  }));

  const cityOptions = (citiesQuery.data ?? []).map((city) => ({
    value: city.code,
    label: city.name,
  }));

  return (
    <div className="space-y-6">
      <Section title="Identity">
        <Row>
          <Field error={errors.firstName?.message}>
            <FieldLabel>First name</FieldLabel>
            <FieldInput autoComplete="off" {...register('firstName')} />
          </Field>

          <Field error={errors.lastName?.message}>
            <FieldLabel>Last name</FieldLabel>
            <FieldInput autoComplete="off" {...register('lastName')} />
          </Field>
        </Row>
      </Section>

      <Section title="Contact">
        <Row>
          <Field error={errors.email?.message}>
            <FieldLabel>Email</FieldLabel>
            <FieldInput type="email" autoComplete="off" {...register('email')} />
          </Field>

          <Field error={errors.phone?.message}>
            <FieldLabel>Phone</FieldLabel>
            <FieldInput type="tel" autoComplete="off" {...register('phone')} />
          </Field>
        </Row>
      </Section>

      <Section title="Profile">
        <Field error={errors.status?.message} required>
          <FieldLabel>Guest status</FieldLabel>
          <Controller
            control={control}
            name="status"
            render={({ field }) => (
              <FieldSelect
                value={field.value}
                onValueChange={field.onChange}
                onBlur={field.onBlur}
                options={GUEST_STATUS_OPTIONS}
                placeholder="Choose a status"
                searchable
              />
            )}
          />
        </Field>
      </Section>

      <Section title="Account">
        {hasAccount ? (
          <Field error={errors.accountStatus?.message}>
            <FieldLabel>Account status</FieldLabel>
            <Controller
              control={control}
              name="accountStatus"
              render={({ field }) => (
                <FieldSelect
                  value={field.value ?? ''}
                  onValueChange={field.onChange}
                  onBlur={field.onBlur}
                  options={GUEST_ACCOUNT_STATUS_OPTIONS}
                  placeholder="Choose an account status"
                  searchable
                />
              )}
            />
          </Field>
        ) : (
          <p className="text-body text-muted-foreground">This guest has no account.</p>
        )}
      </Section>

      <Section title="Address">
        <Row>
          <Field error={errors.address?.type?.message}>
            <FieldLabel>Address type</FieldLabel>
            <Controller
              control={control}
              name="address.type"
              render={({ field }) => (
                <FieldSelect
                  value={field.value ?? ''}
                  onValueChange={field.onChange}
                  onBlur={field.onBlur}
                  options={GUEST_ADDRESS_TYPE_OPTIONS}
                  placeholder="Choose an address type"
                  searchable
                />
              )}
            />
          </Field>

          <Field error={errors.address?.line1?.message}>
            <FieldLabel>Address line 1</FieldLabel>
            <FieldInput autoComplete="off" {...register('address.line1')} />
          </Field>
        </Row>

        <Row>
          <Field error={errors.address?.line2?.message}>
            <FieldLabel>Address line 2</FieldLabel>
            <FieldInput autoComplete="off" {...register('address.line2')} />
          </Field>

          <Field error={errors.address?.postalCode?.message}>
            <FieldLabel>Postcode</FieldLabel>
            <FieldInput autoComplete="off" {...register('address.postalCode')} />
          </Field>
        </Row>

        <Row>
          <Field error={errors.address?.countryCode?.message}>
            <FieldLabel>Country</FieldLabel>
            <Controller
              control={control}
              name="address.countryCode"
              render={({ field }) => (
                <FieldSelect
                  value={field.value ?? ''}
                  onValueChange={(value) => {
                    field.onChange(value);
                    setStateSearch('');
                    setCitySearch('');
                  }}
                  onSearchChange={setCountrySearch}
                  onBlur={field.onBlur}
                  options={countryOptions}
                  placeholder="Choose a country"
                />
              )}
            />
          </Field>

          <Field error={errors.address?.state?.message}>
            <FieldLabel>State / Province</FieldLabel>
            <Controller
              control={control}
              name="address.state"
              render={({ field }) => (
                <FieldSelect
                  value={field.value ?? ''}
                  onValueChange={(value) => {
                    field.onChange(value);
                    setCitySearch('');
                  }}
                  onSearchChange={setStateSearch}
                  onBlur={field.onBlur}
                  options={stateOptions}
                  placeholder="Choose a state"
                />
              )}
            />
          </Field>
        </Row>

        <Row>
          <Field error={errors.address?.city?.message}>
            <FieldLabel>City</FieldLabel>
            <Controller
              control={control}
              name="address.city"
              render={({ field }) => (
                <FieldSelect
                  value={field.value ?? ''}
                  onValueChange={field.onChange}
                  onSearchChange={setCitySearch}
                  onBlur={field.onBlur}
                  options={cityOptions}
                  placeholder="Choose a city"
                  searchable
                />
              )}
            />
          </Field>
        </Row>
      </Section>
    </div>
  );
}
